import { isNumber } from '../shared/runtime-values.js';
import {
  cleanCompetitorChannel,
  cleanCompetitorSource,
  type CompetitorChannel,
  type CompetitorSource,
} from '../shared/competitor-acquisition.js';

export interface CompetitorAttribution {
  source: CompetitorSource;
  channel: CompetitorChannel;
  landingPage: string;
  capturedAt: number;
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const STORAGE_KEY = 'drawonamap:competitor-attribution:v1';
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const LANDING_PAGE_BY_SOURCE = {
  google_my_maps: '/blog/draw-on-google-maps/',
  scribble_maps: '/blog/scribble-maps-alternative/',
  maptive: '/blog/maptive-alternative/',
};

export const COMPETITOR_LABEL_BY_SOURCE = {
  google_my_maps: 'Google My Maps',
  scribble_maps: 'Scribble Maps',
  maptive: 'Maptive',
};

function channelFromUrl(url: URL): CompetitorChannel {
  const explicit = cleanCompetitorChannel(url.searchParams.get('switch_channel'));
  if (explicit) return explicit;

  const medium = (url.searchParams.get('utm_medium') ?? '').toLowerCase();
  const source = (url.searchParams.get('utm_source') ?? '').toLowerCase();
  if (['cpc', 'ppc', 'paid', 'paid_search'].includes(medium)) return 'paid_search';
  if (['community', 'social', 'referral'].includes(medium) || ['reddit', 'hacker_news'].includes(source)) return 'community';
  return 'organic_content';
}

function browserStorage(): StorageLike | null {
  if (!('window' in globalThis)) return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readCompetitorAttribution(
  storage: StorageLike | null = browserStorage(),
  now = Date.now(),
): CompetitorAttribution | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // SAFETY: The following parser checks every field that it accepts from this external payload.
    const parsed = JSON.parse(raw) as Partial<CompetitorAttribution>;
    const source = cleanCompetitorSource(parsed.source);
    const channel = cleanCompetitorChannel(parsed.channel);
    const capturedAt = isNumber(parsed.capturedAt) ? parsed.capturedAt : 0;
    if (!source || !channel || capturedAt <= 0 || now - capturedAt > ATTRIBUTION_TTL_MS || capturedAt > now + 60_000) {
      storage.removeItem(STORAGE_KEY);
      return null;
    }
    return { source, channel, landingPage: LANDING_PAGE_BY_SOURCE[source], capturedAt };
  } catch {
    try { storage.removeItem(STORAGE_KEY); } catch { /* optional storage */ }
    return null;
  }
}

export function captureCompetitorAttribution(
  href = !('window' in globalThis) ? '' : window.location.href,
  storage: StorageLike | null = browserStorage(),
  now = Date.now(),
): CompetitorAttribution | null {
  let url: URL;
  try {
    url = new URL(href, 'https://drawonamap.com/');
  } catch {
    return readCompetitorAttribution(storage, now);
  }
  const source = cleanCompetitorSource(url.searchParams.get('switch_from'));
  if (!source) return readCompetitorAttribution(storage, now);

  const attribution: CompetitorAttribution = {
    source,
    channel: channelFromUrl(url),
    landingPage: LANDING_PAGE_BY_SOURCE[source],
    capturedAt: now,
  };
  if (storage) {
    try { storage.setItem(STORAGE_KEY, JSON.stringify(attribution)); } catch { /* optional storage */ }
  }
  return attribution;
}

export function competitorImportSource(attribution: CompetitorAttribution | null): string {
  return attribution ? `competitor_${attribution.source}` : 'file_picker';
}

export function isCompetitorImportIntent(href = !('window' in globalThis) ? '' : window.location.href): boolean {
  try {
    return new URL(href, 'https://drawonamap.com/').searchParams.get('intent') === 'import';
  } catch {
    return false;
  }
}
